import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, TestTubes, Sparkles, ExternalLink, Loader2 } from 'lucide-react';


export default function PeptideDatabase() {
  const [peptides, setPeptides] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [aiSearching, setAiSearching] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPeptides();
  }, []);

  const fetchPeptides = async () => {
    try {
      const response = await fetch(`${API}/peptides`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPeptides(Array.isArray(data) ? data : []);
      } else {
        console.error('Peptides fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch peptides:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAiSearch = async () => {
    const q = search.trim();
    if (q.length < 2) return;
    setAiSearching(true);
    setAiResult(null);
    try {
      const res = await fetch(`${API}/peptides/ai-search?q=${encodeURIComponent(q)}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult(data);
      } else {
        setAiResult({ source: 'ai_error', message: `HTTP ${res.status}`, results: [] });
      }
    } catch (e) {
      setAiResult({ source: 'ai_error', message: e.message, results: [] });
    } finally {
      setAiSearching(false);
    }
  };

  const filteredPeptides = peptides.filter(p => {
    const name = (p?.name || '').toLowerCase();
    const aliases = Array.isArray(p?.aliases) ? p.aliases : [];
    const term = search.toLowerCase();
    return name.includes(term) || aliases.some(a => (a || '').toLowerCase().includes(term));
  });

  const categories = ['All', ...new Set(peptides.map(p => p?.category).filter(Boolean))];

  const displayPeptides = selectedCategory === 'All'
    ? filteredPeptides
    : filteredPeptides.filter(p => p.category === selectedCategory);

  // Show the "Ask AI" affordance only when there's a search term that returned no local matches
  const showAiCta = search.trim().length >= 2 && filteredPeptides.length === 0 && !loading;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="peptides-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
          Peptide Database
        </h1>
        <p className="text-muted-foreground">
          Comprehensive research peptide information
        </p>
      </div>

      <div className="mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search peptides… (or type any compound and press Ask AI)"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setAiResult(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && showAiCta) runAiSearch(); }}
            className="pl-10"
            data-testid="peptide-search-input"
          />
        </div>

        {showAiCta && (
          <Card className="p-4 glass-card border-primary/30" data-testid="ai-search-cta">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                <p className="text-sm">
                  No local matches for <span className="font-semibold">"{search}"</span>. Ask AI to research this compound?
                </p>
              </div>
              <Button
                size="sm"
                onClick={runAiSearch}
                disabled={aiSearching}
                data-testid="ask-ai-button"
              >
                {aiSearching
                  ? (<><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Asking AI…</>)
                  : (<><Sparkles className="h-3.5 w-3.5 mr-2" /> Ask AI</>)}
              </Button>
            </div>
          </Card>
        )}

        {aiResult && (aiResult.source === 'ai_no_match' || aiResult.source === 'ai_unavailable' || aiResult.source === 'ai_error') && (
          <Card className="p-4 glass-card border-amber-500/30" data-testid="ai-search-empty">
            <p className="text-sm text-muted-foreground">
              {aiResult.message || `No reliable information found for "${search}".`}
            </p>
          </Card>
        )}

        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat.toLowerCase().replace(/\//g, '-')}`}
            >
              {cat}
            </Button>
          ))}
        </div>
      </div>

      {/* AI result card (when AI found a match) */}
      {aiResult && aiResult.source === 'ai' && Array.isArray(aiResult.results) && aiResult.results.length > 0 && (
        <Card className="mb-6 p-6 glass-card border-primary/40" data-testid="ai-search-result">
          <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="text-xl font-bold">{aiResult.results[0].name}</h3>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/40">
                AI-researched
              </span>
            </div>
            {aiResult.results[0].category && (
              <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                {aiResult.results[0].category}
              </span>
            )}
          </div>
          {aiResult.results[0].aliases?.length > 0 && (
            <p className="text-xs text-muted-foreground mb-3">
              Aliases: {aiResult.results[0].aliases.slice(0, 4).join(', ')}
            </p>
          )}
          <p className="text-sm mb-4">{aiResult.results[0].summary}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {aiResult.results[0].dosage_range && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Dosage range</p>
                <p>{aiResult.results[0].dosage_range}</p>
              </div>
            )}
            {aiResult.results[0].half_life && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Half-life</p>
                <p>{aiResult.results[0].half_life}</p>
              </div>
            )}
            {aiResult.results[0].reconstitution && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Reconstitution</p>
                <p>{aiResult.results[0].reconstitution}</p>
              </div>
            )}
            {aiResult.results[0].storage && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Storage</p>
                <p>{aiResult.results[0].storage}</p>
              </div>
            )}
            {aiResult.results[0].benefits?.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Potential benefits</p>
                <ul className="list-disc ml-5 space-y-0.5">
                  {aiResult.results[0].benefits.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
            {aiResult.results[0].side_effects?.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Reported side effects</p>
                <ul className="list-disc ml-5 space-y-0.5">
                  {aiResult.results[0].side_effects.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {aiResult.results[0].warnings && (
              <div className="md:col-span-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200">
                <p className="text-xs font-medium uppercase tracking-wide mb-1">Warnings</p>
                <p>{aiResult.results[0].warnings}</p>
              </div>
            )}
            {aiResult.results[0].pubmed_links?.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">References</p>
                <ul className="space-y-1">
                  {aiResult.results[0].pubmed_links.map((url, i) => (
                    <li key={i}>
                      <a href={url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
                        <ExternalLink className="h-3 w-3" /> {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-4 italic">
            AI-generated research summary. Not in the curated PeptideForge database — verify before use.
          </p>
        </Card>
      )}

      {loading ? (
        <div className="text-center py-12" data-testid="peptides-loading">Loading…</div>
      ) : displayPeptides.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="peptides-empty">
          {peptides.length === 0
            ? 'No peptides loaded. Check your connection and refresh.'
            : 'No peptides match your filters.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayPeptides.map(peptide => (
            <Card
              key={peptide.peptide_id}
              className="p-6 glass-card cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
              onClick={() => navigate(`/peptides/${peptide.peptide_id}`)}
              data-testid={`peptide-card-${peptide.peptide_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <TestTubes className="h-8 w-8 text-primary" />
                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/30">
                  {peptide.category}
                </span>
              </div>
              <h3 className="text-xl font-bold mb-2">{peptide.name}</h3>
              {Array.isArray(peptide.aliases) && peptide.aliases.length > 0 && (
                <p className="text-xs text-muted-foreground mb-3">
                  {peptide.aliases.slice(0, 2).join(', ')}
                </p>
              )}
              <p className="text-sm text-muted-foreground line-clamp-3">
                {peptide.summary}
              </p>
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Dosage: <span className="font-mono text-foreground">{peptide.dosage_range}</span>
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
